import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  styled,
} from '@mui/material';
import { addMonths, format, parse } from 'date-fns';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { updateUser } from '../../../../../api/user';
import { AuthContext } from '../../../../../contexts/AuthContext';
import { FrontSelect } from '../../../../../ui-components';

const options: { value: string; label: string }[] = [
  { value: 'January', label: 'January' },
  { value: 'February', label: 'February' },
  { value: 'March', label: 'March' },
  { value: 'April', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'June', label: 'June' },
  { value: 'July', label: 'July' },
  { value: 'August', label: 'August' },
  { value: 'September', label: 'September' },
  { value: 'October', label: 'October' },
  { value: 'November', label: 'November' },
  { value: 'December', label: 'December' },
];

interface IProps {
  onClose: () => void;
}

export const FiscalYearModal = ({ onClose }: IProps) => {
  const { token, apiUser, setAPIUser } = useContext(AuthContext);

  const [selectedMonth, setSelectedMonth] = useState(options[0]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (apiUser?.settings?.global?.['fiscal-year-start'])
      setSelectedMonth(
        options.find((op) => op.value === apiUser.settings.global['fiscal-year-start']) ||
          options[0],
      );
  }, [apiUser, setSelectedMonth]);

  const updateMonth = useCallback(async () => {
    setIsLoading(true);
    await updateUser(token, {
      setting: 'fiscal-year-start',
      value: selectedMonth.value,
    });
    setAPIUser({
      ...apiUser,
      settings: {
        ...apiUser.settings,
        global: { ...apiUser.settings.global, 'fiscal-year-start': selectedMonth.value },
      },
    });
    setIsLoading(false);
    onClose();
  }, [token, selectedMonth, setIsLoading, onClose, setAPIUser, apiUser]);

  const dateRange = useMemo(() => {
    const dateStart = parse(
      `${selectedMonth?.value}/01/${selectedYear}`,
      'MMMM/dd/yyyy',
      new Date(),
    );
    const dateEnd = addMonths(dateStart, 11);
    return `FY${format(dateStart, 'yyyy')} - ${format(dateEnd, 'yyyy')} (${format(dateStart, 'MMMM')} - ${format(dateEnd, 'MMMM')})`;
  }, [selectedMonth, selectedYear]);

  return (
    <CustomDialog onClose={onClose} open>
      <DialogTitle sx={{ m: 0, p: 1 }}>
        <Title>Fiscal Year Settings</Title>
        <IconButton
          onClick={onClose}
          size={'small'}
          sx={(theme) => ({
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon fontSize={'small'} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Wrapper>
          <Block>
            <IconButton size="small" onClick={() => setSelectedYear(selectedYear - 1)}>
              <RemoveIcon fontSize={'small'} />
            </IconButton>
            <FormControl variant="standard" sx={{ flex: 1 }}>
              <InputLabel>Current Fiscal year</InputLabel>
              <Input size={'small'} disabled value={dateRange} />
            </FormControl>
            <IconButton
              type="button"
              size="small"
              onClick={() => setSelectedYear(selectedYear + 1)}
            >
              <AddIcon fontSize={'small'} />
            </IconButton>
          </Block>
          <Block2>
            <InputLabel>Fiscal Year Start Month</InputLabel>
            <FrontSelect
              options={options}
              value={selectedMonth}
              onChange={(v) => v && setSelectedMonth(v)}
            />
          </Block2>
        </Wrapper>
      </DialogContent>
      <DialogActions>
        <Button
          size={'small'}
          variant={'contained'}
          onClick={onClose}
          loading={isLoading}
          loadingPosition={'start'}
          color="inherit"
        >
          Close
        </Button>
        <Button
          size={'small'}
          variant={'contained'}
          loading={isLoading}
          loadingPosition={'start'}
          onClick={updateMonth}
          color="primary"
        >
          Save Changes
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

const CustomDialog = styled(Dialog)(() => ({
  '& .MuiDialogTitle-root': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
}));

const Title = styled('div')``;

const Wrapper = styled('div')`
  width: 400px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Block = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Block2 = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;
`;
